import jsPDF from 'jspdf';

/*
  Etiquetas de producto — layout según altura configurada (3 niveles):

  • Con layout guardado: usa posiciones absolutas (resultado del editor drag).
  • Mínimo   (height < 15mm, ej. Niimbot 40×12mm): QR izq | texto der.
  • Horizontal (15–25mm): QR izq | nombre/variación·precio/sku der.
  • Vertical  (>25mm): tienda/nombre/variación/QR/precio/sku centrados.
*/

export const DEFAULT_LABEL_CONFIG = { width: 40, height: 12 };

export const QR_SCALE = { min: 0.45, max: 1, step: 0.025, default: 1 };

function clampQrScale(v) {
  const n = typeof v === 'number' && isFinite(v) ? v : QR_SCALE.default;
  return Math.min(QR_SCALE.max, Math.max(QR_SCALE.min, n));
}

function lhMm(sizePt) { return sizePt * 0.424; }

function pickFontSize(doc, text, maxWidth, startSize, minSize, maxLines) {
  let size = startSize;
  while (size > minSize) {
    doc.setFontSize(size);
    if (doc.splitTextToSize(text, maxWidth).length <= maxLines) break;
    size -= 0.5;
  }
  doc.setFontSize(size);
  return size;
}

function renderCentered(doc, lines, startY, lineH, centerX) {
  lines.forEach((line, i) => {
    doc.text(line, centerX, startY + lineH * 0.65 + i * lineH, { align: 'center', baseline: 'middle' });
  });
}

function toFileName(str) {
  return (str || '')
    .normalize('NFD').split('').filter(c => { const n = c.charCodeAt(0); return n < 0x0300 || n > 0x036F; }).join('')
    .replace(/[^a-zA-Z0-9\s]/g, '').trim().replace(/\s+/g, '-').replace(/-+/g, '-');
}

function buildFilename(items) {
  if (items.length > 1) return 'Etiquetas_Lote_' + new Date().toISOString().slice(0, 10) + '_' + items.length + '-productos.pdf';
  const item = items[0];
  const namePart = toFileName(item.productName);
  const varPart  = item.variationLabel ? '_' + toFileName(item.variationLabel) : '';
  const idPart   = item.sku ? 'SKU-' + toFileName(item.sku) : String(item.variationId || item.productId || 'id');
  return 'Etiqueta_' + namePart + varPart + '_' + idPart + '.pdf';
}

// ─────────────────────────────────────────────────────────────────────────────
// LAYOUT ABSOLUTO — usa las posiciones guardadas por el editor drag.
// ─────────────────────────────────────────────────────────────────────────────
function addLabelPageAbsolute(doc, item, isFirst, cfg) {
  const W = cfg.width, H = cfg.height;
  if (!isFirst) doc.addPage([W, H], W > H ? 'landscape' : 'portrait');

  const { layout } = item;

  if (layout.qr && item.qrDataUrl) {
    const q = layout.qr;
    doc.addImage(item.qrDataUrl, 'PNG', q.x, q.y, q.size, q.size);
  }

  const price = (item.price || '').trim();
  const name  = (item.productName || '').trim();
  const varia = (item.variationLabel || '').trim();

  if (layout.price && price) {
    const p = layout.price;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(p.fontPt);
    doc.setTextColor(0, 0, 0);
    doc.text(price, p.x, p.y + lhMm(p.fontPt) * 0.72);
  }
  if (layout.name && name) {
    const p = layout.name;
    doc.setFont('helvetica', 'normal'); // Typography A
    doc.setFontSize(p.fontPt);
    doc.setTextColor(17, 17, 17);
    doc.text(name, p.x, p.y + lhMm(p.fontPt) * 0.72);
  }
  if (layout.variation && varia) {
    const p = layout.variation;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(p.fontPt);
    doc.setTextColor(85, 85, 85);
    doc.text(varia, p.x, p.y + lhMm(p.fontPt) * 0.72);
  }
  doc.setTextColor(0, 0, 0);
}

// ─────────────────────────────────────────────────────────────────────────────
// LAYOUT 0 — Mínimo (height < 15mm)
// ─────────────────────────────────────────────────────────────────────────────
const M0_SIDE = 1, M0_TB = 1, M0_GAP = 1.5, M0_LGAP = 0.5;

function addLabelPageMinimal(doc, item, isFirst, cfg) {
  const W = cfg.width, H = cfg.height;
  if (!isFirst) doc.addPage([W, H], W > H ? 'landscape' : 'portrait');

  const qrSize = (H * 0.95) * clampQrScale(item.qrScale);
  const qrY    = (H - qrSize) / 2;
  if (item.qrDataUrl) doc.addImage(item.qrDataUrl, 'PNG', M0_SIDE, qrY, qrSize, qrSize);

  const textX = M0_SIDE + qrSize + M0_GAP;
  const textW = W - textX - M0_SIDE;
  if (textW < 4) return;

  const price = (item.price || '').trim();
  const name  = (item.productName || '').trim();
  const varia = (item.variationLabel || '').trim();
  const code  = [item.sku, item.loteNumero].filter(Boolean).join(' · ');

  const prStart = typeof item.priceSize === 'number' ? item.priceSize : 9;
  const nStart  = typeof item.nameSize  === 'number' ? item.nameSize  : 4;
  const vStart  = typeof item.varSize   === 'number' ? item.varSize   : 3.5;

  doc.setFont('helvetica', 'bold');
  const prSz = price ? pickFontSize(doc, price, textW, prStart, 6.5, 1) : 0;
  doc.setFont('helvetica', 'normal'); // Typography A: nombre en normal
  const nSz  = name  ? pickFontSize(doc, name,  textW, nStart,  2.8, 1) : 0;
  const vSz  = varia ? pickFontSize(doc, varia, textW, vStart,  2.5, 1) : 0;
  const cSz  = 2.5;

  const usableH = H - 2 * M0_TB;
  const rows = [];
  let totalH = 0;
  if (price) { rows.push({ txt: doc.splitTextToSize(price, textW)[0], sz: prSz, lh: lhMm(prSz), w: 'bold',   c: [0,0,0]     }); totalH += lhMm(prSz); }
  if (name)  { if (rows.length) totalH += M0_LGAP; rows.push({ txt: doc.splitTextToSize(name, textW)[0],  sz: nSz,  lh: lhMm(nSz),  w: 'normal', c: [17,17,17]  }); totalH += lhMm(nSz); }
  if (varia) { if (rows.length) totalH += M0_LGAP; rows.push({ txt: doc.splitTextToSize(varia, textW)[0], sz: vSz,  lh: lhMm(vSz),  w: 'normal', c: [85,85,85]  }); totalH += lhMm(vSz); }
  if (code && totalH + M0_LGAP + lhMm(cSz) <= usableH) {
    rows.push({ txt: doc.splitTextToSize(code, textW)[0], sz: cSz, lh: lhMm(cSz), w: 'normal', c: [150,150,150] });
    totalH += M0_LGAP + lhMm(cSz);
  }

  let y = M0_TB + Math.max(0, (usableH - totalH) / 2);
  rows.forEach((r, i) => {
    if (i > 0) y += M0_LGAP;
    doc.setFont('helvetica', r.w); doc.setFontSize(r.sz); doc.setTextColor(...r.c);
    doc.text(r.txt, textX, y + r.lh * 0.72);
    y += r.lh;
  });
  doc.setTextColor(0, 0, 0);
}

// ─────────────────────────────────────────────────────────────────────────────
// LAYOUT 1 — Horizontal (15–25mm)
// ─────────────────────────────────────────────────────────────────────────────
function addLabelPageHorizontal(doc, item, isFirst, cfg) {
  const W = cfg.width, H = cfg.height;
  if (!isFirst) doc.addPage([W, H]);

  const SIDE = 1, TB = 1.5, GAP = 1.5;
  const usableH = H - 2 * TB;
  const qrSize  = usableH * clampQrScale(item.qrScale);
  const qrX = SIDE, qrY = TB + (usableH - qrSize) / 2;
  const textX = SIDE + qrSize + GAP, textW = W - textX - SIDE;
  if (item.qrDataUrl) doc.addImage(item.qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);

  const nStart  = typeof item.nameSize  === 'number' ? item.nameSize  : 4.5;
  const l2Start = typeof item.priceSize === 'number' ? item.priceSize : 3.5;

  doc.setFont('helvetica', 'normal'); // Typography A
  const nameSz   = pickFontSize(doc, item.productName || '', textW, nStart, 2.8, 1);
  const nameText = doc.splitTextToSize(item.productName || '', textW)[0] || '';
  const nameLH   = lhMm(nameSz);

  const varPart = (item.variationLabel || '').trim(), pricePart = (item.price || '').trim();
  const line2   = [varPart, pricePart].filter(Boolean).join(' · ');
  const line2Sz = line2 ? pickFontSize(doc, line2, textW, l2Start, 2.3, 1) : 0;
  const line2LH = line2Sz ? lhMm(line2Sz) : 0;
  const line3   = [item.sku, item.loteNumero].filter(Boolean).join(' · ');
  const line3Sz = 2.5, line3LH = line3 ? lhMm(line3Sz) : 0;

  const totalTH = nameLH + (line2LH ? 0.6 + line2LH : 0) + (line3LH ? 0.5 + line3LH : 0);
  let y = TB + Math.max(0, (usableH - totalTH) / 2);

  doc.setFont('helvetica', 'normal'); doc.setFontSize(nameSz); doc.setTextColor(0,0,0);
  doc.text(nameText, textX, y + nameLH * 0.72); y += nameLH;

  if (line2) {
    y += 0.6; doc.setFontSize(line2Sz);
    if (varPart && pricePart) {
      doc.setFont('helvetica', 'normal');
      const sep = varPart + ' · ';
      doc.text(sep, textX, y + line2LH * 0.72);
      doc.setFont('helvetica', 'bold');
      doc.text(pricePart, textX + doc.getTextWidth(sep), y + line2LH * 0.72);
    } else {
      doc.setFont('helvetica', pricePart ? 'bold' : 'normal');
      doc.text(line2, textX, y + line2LH * 0.72);
    }
    y += line2LH;
  }
  if (line3) {
    y += 0.5; doc.setFont('helvetica', 'normal'); doc.setFontSize(line3Sz);
    doc.setTextColor(150,150,150); doc.text(line3, textX, y + line3LH * 0.72); doc.setTextColor(0,0,0);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// LAYOUT 2 — Vertical (>25mm)
// ─────────────────────────────────────────────────────────────────────────────
function addLabelPageVertical(doc, item, isFirst, cfg) {
  const W = cfg.width, H = cfg.height;
  if (!isFirst) doc.addPage([W, H]);

  const VM = 1.5, VT = 1.5, cw = W - VM * 2, cx = W / 2;
  let y = VT;

  if (item.storeName) {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(3); doc.setTextColor(130,130,130);
    doc.text(item.storeName.toUpperCase(), cx, y + lhMm(3) * 0.65, { align: 'center', baseline: 'middle' });
    doc.setTextColor(0,0,0); y += lhMm(3) + 1.0;
  }

  const nStart = typeof item.nameSize === 'number' ? item.nameSize : 4.5;
  doc.setFont('helvetica', 'normal'); // Typography A
  const nameSz    = pickFontSize(doc, item.productName || '', cw, nStart, 2.8, 2);
  const nameLines = doc.splitTextToSize(item.productName || '', cw).slice(0, 2);
  renderCentered(doc, nameLines, y, lhMm(nameSz), cx);
  y += nameLines.length * lhMm(nameSz) + 1.0;

  if (item.variationLabel) {
    const vStart = typeof item.varSize === 'number' ? item.varSize : 3.5;
    doc.setFont('helvetica', 'normal'); doc.setTextColor(80,80,80);
    const varSz    = pickFontSize(doc, item.variationLabel, cw, vStart, 2.5, 2);
    const varLines = doc.splitTextToSize(item.variationLabel, cw);
    renderCentered(doc, varLines, y, lhMm(varSz), cx);
    doc.setTextColor(0,0,0); y += varLines.length * lhMm(varSz) + 1.2;
  }

  if (item.qrDataUrl) {
    const qrMax  = Math.min(cw, (H - y - VM - lhMm(5.5) - 1.0 - lhMm(2.5) - 0.6));
    const qrSize = qrMax * clampQrScale(item.qrScale);
    if (qrSize >= 5) {
      y += 0.6; doc.addImage(item.qrDataUrl, 'PNG', (W - qrSize) / 2, y, qrSize, qrSize);
      y += qrSize + 1.5;
    }
  }

  if (item.price) {
    const prSize = typeof item.priceSize === 'number' ? item.priceSize : 5.5;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(prSize);
    doc.text(item.price, cx, y + lhMm(prSize) * 0.65, { align: 'center', baseline: 'middle' });
    y += lhMm(prSize) + 0.8;
  }

  const line3 = [item.sku, item.loteNumero].filter(Boolean).join(' · ');
  if (line3) {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(2.5); doc.setTextColor(140,140,140);
    doc.text(line3, cx, y + lhMm(2.5) * 0.65, { align: 'center', baseline: 'middle' });
    doc.setTextColor(0,0,0);
  }
}

function addLabelPage(doc, item, isFirst, cfg) {
  if (item.layout && Object.keys(item.layout).length > 0) {
    addLabelPageAbsolute(doc, item, isFirst, cfg);
  } else if (cfg.height < 15) {
    addLabelPageMinimal(doc, item, isFirst, cfg);
  } else if (cfg.height <= 25) {
    addLabelPageHorizontal(doc, item, isFirst, cfg);
  } else {
    addLabelPageVertical(doc, item, isFirst, cfg);
  }
}

function buildDoc(items, cfg) {
  const orientation = cfg.width > cfg.height ? 'landscape' : 'portrait';
  const doc = new jsPDF({ unit: 'mm', format: [cfg.width, cfg.height], orientation });
  items.forEach((item, i) => addLabelPage(doc, item, i === 0, cfg));
  return doc;
}

export function generateAndPrintLabels(items, config) {
  if (!items || items.length === 0) return;
  const cfg = { ...DEFAULT_LABEL_CONFIG, ...(config || {}) };
  const doc = buildDoc(items, cfg);
  doc.autoPrint(); doc.output('dataurlnewwindow');
}

export function generateAndDownloadLabels(items, config) {
  if (!items || items.length === 0) return;
  const cfg = { ...DEFAULT_LABEL_CONFIG, ...(config || {}) };
  buildDoc(items, cfg).save(buildFilename(items));
}

